import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Buildings, UserPlus, ArrowClockwise, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
export function genPassword() {
  const U = "ABCDEFGHJKMNPQRSTUVWXYZ", L = "abcdefghijkmnpqrstuvwxyz", D = "23456789", S = "!@#$%^&*?";
  const all = U + L + D + S;
  let p = [U, L, D, S].map((set) => set[Math.floor(Math.random() * set.length)]);
  for (let i = 0; i < 14; i++) p.push(all[Math.floor(Math.random() * all.length)]);
  return p.sort(() => Math.random() - 0.5).join("");
}

function AdminFields({ form, setForm }) {
  return (
    <>
      <div><Label className="text-xs">Admin name</Label>
        <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
          className="rounded-none mt-1" placeholder="Jane Doe" data-testid="onboard-admin-name" /></div>
      <div><Label className="text-xs">Admin email (login)</Label>
        <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
          className="rounded-none mt-1" placeholder="jane@customer.com" data-testid="onboard-admin-email" /></div>
      <div><Label className="text-xs">Temporary password</Label>
        <div className="flex gap-2 mt-1">
          <Input value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
            className="rounded-none font-mono" placeholder="min 14 chars" data-testid="onboard-admin-password" />
          <Button type="button" variant="outline" className="rounded-none shrink-0"
            onClick={() => setForm({ ...form, admin_password: genPassword() })} data-testid="onboard-generate-password">
            <ArrowClockwise size={14} className="mr-1" /> Generate</Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-1">New admin must change this on first login. Leave blank if this person already has a ClaroShield login — they'll be added to this entity and keep their current password.</p>
      </div>
    </>
  );
}

export function NewTenantDialog({ open, onOpenChange, plans = [], onCreated }) {
  const defaultPlan = useMemo(() => (plans.find((p) => p.key === "free_trial")?.key) || plans[0]?.key || "free_trial", [plans]);
  const [form, setForm] = useState({ name: "", nerc_id: "", plan: "free_trial", slug: "", slugTouched: false,
                                     admin_name: "", admin_email: "", admin_password: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ name: "", nerc_id: "", plan: defaultPlan, slug: "", slugTouched: false,
                admin_name: "", admin_email: "", admin_password: "" });
      setResult(null);
    }
  }, [open, defaultPlan]);

  const onName = (v) => setForm((f) => ({ ...f, name: v, slug: f.slugTouched ? f.slug : slugify(v) }));
  const create = async () => {
    if (!form.name.trim() || !form.admin_name.trim() || !form.admin_email.trim())
      return toast.error("Entity name, admin name and email are required.");
    setBusy(true);
    try {
      const { data } = await api.post("/console/tenants", {
        name: form.name, nerc_id: form.nerc_id, plan: form.plan, slug: form.slug || undefined,
        admin_name: form.admin_name, admin_email: form.admin_email, admin_password: form.admin_password || undefined,
      });
      setResult({ ...data, password: form.admin_password });
      toast.success(data.admin?.attached ? "Tenant created — existing user added as admin" : "Tenant created");
      onCreated && onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create tenant");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-tenant-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Buildings size={18} /> New Tenant (Create Entity)</DialogTitle>
          <DialogDescription>Onboard a customer: create the entity and its first Admin account.</DialogDescription></DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="new-tenant-result">
            <div className="flex items-center gap-2 text-emerald-700"><CheckCircle size={20} weight="fill" /><span className="font-semibold">{result.tenant?.name} created</span></div>
            {result.admin?.attached ? (
              <>
                <div className="border border-slate-200 bg-slate-50 p-3 text-sm space-y-1" data-testid="new-tenant-attached">
                  <div><span className="text-slate-500">Subdomain:</span> <span className="font-mono">{result.subdomain}</span></div>
                  <div><span className="text-slate-500">Admin:</span> <span className="font-mono">{result.admin?.email}</span></div>
                </div>
                <div className="flex items-start gap-2 text-[12px] text-emerald-800 bg-emerald-50 border border-emerald-200 p-2">
                  <CheckCircle size={16} className="shrink-0 mt-0.5" />
                  <span><b>{result.admin?.email}</b> already had a ClaroShield login and was added as an admin of this entity. They sign in with their <b>existing password</b> — no new credentials needed. After signing in they can switch between their entities from the sidebar.</span>
                </div>
                <Button className="rounded-none w-full" onClick={() => onOpenChange(false)} data-testid="new-tenant-done">Done</Button>
              </>
            ) : (
              <>
                <div className="border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
                  <div><span className="text-slate-500">Subdomain:</span> <span className="font-mono">{result.subdomain}</span></div>
                  <div><span className="text-slate-500">Admin login:</span> <span className="font-mono">{result.admin?.email}</span></div>
                  <div><span className="text-slate-500">Temp password:</span> <span className="font-mono font-semibold">{result.password}</span></div>
                </div>
                <div className="flex items-start gap-2 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 p-2">
                  <WarningCircle size={16} className="shrink-0 mt-0.5" />
                  <span>Copy and share these credentials securely now — the password isn't stored and won't be shown again. The admin must change it on first login. (Subdomain routing stays off until wildcard DNS is enabled.)</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="rounded-none flex-1" data-testid="new-tenant-copy"
                    onClick={() => { navigator.clipboard?.writeText(`Subdomain: ${result.subdomain}\nLogin: ${result.admin?.email}\nTemp password: ${result.password}`); toast.success("Credentials copied"); }}>
                    Copy credentials
                  </Button>
                  <Button className="rounded-none flex-1" onClick={() => onOpenChange(false)} data-testid="new-tenant-done">Done</Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs font-semibold text-slate-600 uppercase">Entity</div>
            <div><Label className="text-xs">Entity name</Label>
              <Input value={form.name} onChange={(e) => onName(e.target.value)} className="rounded-none mt-1" placeholder="Acme Power LLC" data-testid="onboard-entity-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">NERC ID</Label>
                <Input value={form.nerc_id} onChange={(e) => setForm({ ...form, nerc_id: e.target.value })} className="rounded-none mt-1" placeholder="NCR12345" data-testid="onboard-nerc-id" /></div>
              <div><Label className="text-xs">Plan</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="onboard-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>{plans.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs">Subdomain slug</Label>
              <div className="flex items-center mt-1">
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value), slugTouched: true })}
                  className="rounded-none font-mono" placeholder="acme-power" data-testid="onboard-slug" />
                <span className="text-xs text-slate-400 ml-2 whitespace-nowrap">.app.clarocompliance.net</span>
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Reserved now; live routing turns on later with wildcard DNS.</p>
            </div>
            <div className="text-xs font-semibold text-slate-600 uppercase pt-2">Initial Admin</div>
            <AdminFields form={form} setForm={setForm} />
            <Button className="rounded-none w-full bg-primary hover:bg-primary/90" onClick={create} disabled={busy} data-testid="onboard-create-btn">
              {busy ? "Creating…" : "Create tenant & admin"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TenantAdminsDialog({ open, onOpenChange, tenant, onChanged }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ admin_name: "", admin_email: "", admin_password: "" });

  const load = useCallback(async () => {
    if (!tenant) return;
    setLoading(true);
    try { const { data } = await api.get(`/console/tenants/${tenant.id}`); setAdmins(data.admins || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load admins"); }
    finally { setLoading(false); }
  }, [tenant]);
  useEffect(() => { if (open) { setAdding(false); setForm({ admin_name: "", admin_email: "", admin_password: "" }); load(); } }, [open, load]);

  const add = async () => {
    if (!form.admin_name.trim() || !form.admin_email.trim() || !form.admin_password) return toast.error("Name, email and password are required.");
    setBusy(true);
    try {
      const { data } = await api.post(`/console/tenants/${tenant.id}/admins`, form);
      toast.success(data.admin?.attached ? "Existing user promoted to admin" : "Admin created");
      setAdding(false); setForm({ admin_name: "", admin_email: "", admin_password: "" });
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add admin"); }
    finally { setBusy(false); }
  };

  if (!tenant) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="tenant-admins-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus size={18} /> Admins — {tenant.name}</DialogTitle>
          <DialogDescription>Add or assign an Admin for this tenant (e.g. when the sole admin leaves).</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {loading ? <div className="text-sm text-slate-500">Loading…</div> : (
            <div className="border border-slate-200 divide-y divide-slate-100" data-testid="tenant-admins-list">
              {admins.length === 0 && <div className="p-3 text-sm text-amber-700" data-testid="tenant-admins-empty">No admins on this tenant — add one below.</div>}
              {admins.map((a) => (
                <div key={a.id} className="flex items-center justify-between p-2.5 text-sm" data-testid={`tenant-admin-${a.id}`}>
                  <div><div className="font-medium">{a.name}</div><div className="text-xs text-slate-500">{a.email}</div></div>
                  <div className="text-right text-[11px]">
                    {a.must_change_password && <span className="text-amber-700 border border-amber-200 bg-amber-50 px-1.5 py-0.5">must set password</span>}
                    <div className="text-slate-400 mt-0.5">{a.last_login ? "active" : "never logged in"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {adding ? (
            <div className="space-y-3 border border-slate-200 p-3">
              <AdminFields form={form} setForm={setForm} />
              <div className="flex gap-2">
                <Button className="rounded-none flex-1" onClick={add} disabled={busy} data-testid="tenant-add-admin-submit">{busy ? "Adding…" : "Add admin"}</Button>
                <Button variant="outline" className="rounded-none" onClick={() => setAdding(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" className="rounded-none w-full" onClick={() => { setForm({ admin_name: "", admin_email: "", admin_password: genPassword() }); setAdding(true); }} data-testid="tenant-add-admin-btn">
              <UserPlus size={15} className="mr-1" /> Add / assign an admin
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
